import React, { useMemo } from "react";
import { AgGrid, AgGridReact, Card, Flex, Grid, Text } from "@jsq/ds/core";
import { useGetPaymentBatchesForDistributionQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { usePaymentsContext } from "../../PaymentsListWorkflow/Context/PaymentsContext";
import { getColumnDefinitions } from "./columnDefinitions";
import { cardTitle, paymentBatchesCard, tableContainer } from "./styles_vanilla.css";
import { PaymentBatchRowItem } from "./types";
import { mapEdgesToRowData } from "./utils";

export type PaymentBatchesTableProps = {
  distributionBatchId: string;
  /** Callback when the Review button is clicked on a payment batch */
  onReviewClick?: (paymentBatch: PaymentBatchRowItem) => void;
};

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  suppressCellFocus: true,
  suppressContextMenu: true,
  domLayout: "autoHeight",
};

const defaultColumnOptions: AgGrid.ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
};

/**
 * Renders the AG-Grid table with payment batches for a specific distribution batch
 * Displays Created Date, Positions, Status, Amount, and Action columns
 * The Action column is only shown if at least one payment batch requires review
 * @returns PaymentBatchesTable
 */
export const PaymentBatchesTable: React.FC<PaymentBatchesTableProps> = ({
  distributionBatchId,
  onReviewClick,
}) => {
  const { isGPX } = usePaymentsContext();

  const { data, loading } = useGetPaymentBatchesForDistributionQuery({
    variables: {
      inputFilter: {
        distributionBatchIds: [distributionBatchId],
      },
    },
    context: clientContext,
    fetchPolicy: "network-only",
  });

  const rowData = useMemo<PaymentBatchRowItem[]>(() => {
    const edges = data?.paymentBatchesConnection.edges ?? [];
    return mapEdgesToRowData(edges, isGPX);
  }, [data, isGPX]);

  // Determine if any batch has a "review" action to show/hide the Action column
  const showActionColumn = useMemo(() => {
    return rowData.some((batch) => batch.action === "review");
  }, [rowData]);

  // Get column definitions based on whether any batch requires review
  const columnDefs = useMemo(() => {
    return getColumnDefinitions(showActionColumn, { onReviewClick });
  }, [showActionColumn, onReviewClick]);

  const noRowsTemplate = `<span>No payment batches have been created</span>`;

  return (
    <Card className={paymentBatchesCard}>
      <Flex flexDirection="column">
        <Text className={cardTitle} variant="secondary-md">
          PAYMENT BATCHES
        </Text>
        <Flex className={tableContainer}>
          <Grid<PaymentBatchRowItem>
            key={showActionColumn ? "with-action" : "without-action"}
            columnDefs={columnDefs}
            defaultColDef={defaultColumnOptions}
            getRowId={(params) => params.data.id}
            gridId="payment_batches_for_distribution"
            gridOptions={gridOptions}
            overlayNoRowsTemplate={noRowsTemplate}
            reactiveCustomComponents
            rowData={loading ? null : rowData}
          />
        </Flex>
      </Flex>
    </Card>
  );
};

export default PaymentBatchesTable;
